import { Controller } from "@hotwired/stimulus";

// Switches the prices on the page between month and year, and between currencies, without a
// reload. Every figure is already on the page in the currency the server picked; the payload
// below holds the same figures for every currency and both periods, formatted server-side --
// so this controller only ever swaps text. Formatting in two places is how the two drift apart.
//
// Both controls are real links, so the page works without this controller: the server answers
// ?periode= and ?currency= itself.
export default class extends Controller {
  static targets = ["payload", "periodTab", "taxNote"];
  static values = { period: String, currency: String };

  connect() {
    this.prices = this.readPayload();
    this.element.addEventListener("currency:changed", this.onCurrencyChanged);
    window.addEventListener("currency:changed", this.onCurrencyChanged);
  }

  disconnect() {
    this.element.removeEventListener("currency:changed", this.onCurrencyChanged);
    window.removeEventListener("currency:changed", this.onCurrencyChanged);
  }

  readPayload() {
    if (!this.hasPayloadTarget) return {};

    try {
      return JSON.parse(this.payloadTarget.textContent);
    } catch (error) {
      return {};
    }
  }

  setPeriod(event) {
    const period = event.currentTarget.dataset.period;
    if (!period) return;

    event.preventDefault();
    this.periodValue = period;
    this.render();
    this.rememberPeriodInUrl(period);
  }

  onCurrencyChanged = (event) => {
    if (!event.detail || !event.detail.currency) return;

    this.currencyValue = event.detail.currency;
    this.render();
  };

  render() {
    const figures = this.figures();
    if (!figures) return;

    this.element.querySelectorAll("[data-price]").forEach((node) => {
      const value = this.lookup(figures, node.dataset.price);
      if (value !== undefined) node.textContent = value;
    });

    this.periodTabTargets.forEach((tab) => {
      const current = tab.dataset.period === this.periodValue;
      tab.classList.toggle("is-current", current);
      tab.setAttribute("aria-current", current ? "true" : "false");
    });

    this.renderMeta();
  }

  // What changes with the currency besides the figures: the discount named in the yearly tab
  // and whether the VAT line applies. Worded by the server, like the amounts.
  renderMeta() {
    const currency = this.prices[this.currencyValue];
    const meta = currency && currency.meta;
    if (!meta) return;

    this.periodTabTargets
      .filter((tab) => tab.dataset.period === "year")
      .forEach((tab) => (tab.textContent = meta.yearly_label));

    this.taxNoteTargets.forEach((note) => (note.hidden = !meta.tax_note));
  }

  figures() {
    const currency = this.prices[this.currencyValue];

    return currency ? currency[this.periodValue] : null;
  }

  // "examples.houses" reaches into the nested part of the payload.
  lookup(figures, key) {
    return key.split(".").reduce((value, part) => (value == null ? value : value[part]), figures);
  }

  // A shareable URL for the yearly prices, without adding a history entry for a toggle.
  rememberPeriodInUrl(period) {
    const url = new URL(window.location.href);
    if (period === "year") {
      url.searchParams.set("periode", "jaar");
    } else {
      url.searchParams.delete("periode");
    }

    window.history.replaceState({}, "", url);
  }
}
